import React, { useState } from 'react';
import { TreeSelect, ConfigProvider } from 'antd';
import Tooltip from '../common/Tooltip';
import { MenuOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useStore } from '../../store/store';
import { TitlesTypes } from '../../constants/constants';
import { navbarElementStyle } from '../../utils/styles';
import { getSummaryLevels } from './SummarySelector';
import StackedLabelField from '../common/StackedLabelField';
import './summaryLevelSelector.css';

const TITLES = Object.values(TitlesTypes);

const selector = ({ summaryConfiguration, updateBox, currentProposal }) => ({
  summaryConfiguration,
  updateBox,
  currentProposal,
});

const applyVisibilityRules = (prevSelected, newValues) => {
  const prevSet = new Set(prevSelected);
  const newSet = new Set(newValues);

  const added = newValues.filter((v) => !prevSet.has(v));
  const removed = [...prevSet].filter((v) => !newSet.has(v));

  const finalSet = new Set(prevSet);

  added.forEach((titleType) => {
    finalSet.add(titleType);
    if (titleType === 'title3') {
      finalSet.add('title2');
    }
    if (titleType === 'title4') {
      finalSet.add('title2');
      finalSet.add('title3');
    }
  });

  removed.forEach((titleType) => {
    if (titleType === 'title1') return;
    finalSet.delete(titleType);
    if (titleType === 'title2') {
      finalSet.delete('title3');
      finalSet.delete('title4');
    }
    if (titleType === 'title3') {
      finalSet.delete('title4');
    }
  });

  finalSet.add('title1');
  return TITLES.filter((t) => finalSet.has(t));
};

export const SummaryLevelSelector = ({ selectedBox }) => {
  const { summaryConfiguration, updateBox, currentProposal } =
    useStore(selector);
  // Niveaux propres au sommaire sélectionné : régler l'un ne change pas les autres.
  const summaryBox = useStore((state) =>
    state.boxes.find((b) => b.id === selectedBox?.id)
  );
  const { t } = useTranslation();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (currentProposal?.id == null) return null;

  const selectedLevels = getSummaryLevels(summaryBox, summaryConfiguration);

  const treeData = TITLES.map((titleType, idx) => ({
    title: t('summary-title-level', { level: idx + 1 }),
    value: titleType,
    key: titleType,
    disableCheckbox: idx === 0,
    checkable: true,
  }));

  const handleChange = (nextValues) => {
    const reconciled = applyVisibilityRules(selectedLevels, nextValues);
    if (!summaryBox) return;
    updateBox(summaryBox.id, (box) => {
      box.content.configuration.visibleLevels = reconciled;
    });
  };

  const disabled = selectedBox?.type !== 'summary';

  return (
    <StackedLabelField label={t('summary-levels')}>
      <div
        style={{ display: 'flex', alignItems: 'center', width: '100%' }}
        onMouseEnter={() => !dropdownOpen && setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
      >
        <ConfigProvider
          theme={{
            components: {
              Select: {
                colorTextDisabled: 'gray',
                colorBgContainerDisabled: 'white',
              },
              TreeSelect: {
                colorTextDisabled: 'gray',
                colorBgContainerDisabled: 'white',
              },
            },
          }}
        >
          <Tooltip
            open={tooltipVisible && !dropdownOpen}
            placement="bottom"
            mouseLeaveDelay="0"
            title={t('summary-levels-tooltip')}
          >
            <TreeSelect
              className="navbar-selector summary-level-selector"
              popupClassName="summary-level-selector-popup"
              popupMatchSelectWidth
              switcherIcon={() => null}
              style={{
                ...navbarElementStyle,
                width: '100%',
              }}
              value={selectedLevels}
              treeData={treeData}
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_ALL}
              treeDefaultExpandAll
              disabled={disabled}
              open={dropdownOpen}
              onOpenChange={(open) => {
                setDropdownOpen(open);
                if (open) setTooltipVisible(false);
              }}
              onChange={handleChange}
              suffixIcon={<MenuOutlined />}
              allowClear={false}
              showSearch={false}
              treeNodeLabelProp="title"
              maxTagCount={0}
              maxTagPlaceholder={(omitted) => {
                const count = omitted.length;
                if (count === TITLES.length) return t('summary-levels-all');
                const levels = omitted
                  .map((item) => {
                    const v = typeof item === 'object' ? item.value : item;
                    return v?.slice(-1);
                  })
                  .filter(Boolean)
                  .join(', ');
                return t('summary-levels-selected', { levels });
              }}
            />
          </Tooltip>
        </ConfigProvider>
      </div>
    </StackedLabelField>
  );
};

export default SummaryLevelSelector;
